import React from 'react';
import { Trans } from '@gamiphy/react-i18n';
import { Stack, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { formatCashbackPromotionValue } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';

const CashbackPercentage: React.FC = () => {
  const title = React.useMemo(() => {
    switch (AppStore.app.agent) {
      case 'wake':
        return (
          <Typography fontSize={18} fontWeight={700} textTransform="uppercase">
            <Trans
              i18nKey="coreloyalty.cashback.value.predefinedPromotions"
              defaults="Up to {{promotionValue}} cashback"
              values={{
                promotionValue: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
              }}
            />
          </Typography>
        );
      default:
        return (
          <Typography fontSize={28} fontWeight={700} textTransform="uppercase">
            <Trans
              i18nKey="fisico.cashbackForm.preview.cashback"
              defaults="{{percentage}}% cashback"
              values={{
                percentage: AppCashbackStore.currentAppCashback?.percentage,
              }}
            />
          </Typography>
        );
    }
  }, [
    AppStore.app.agent,
    AppCashbackStore.currentAppCashback,
    AppCashbackStore.highestPromotionAppCashback,
  ]);

  const description = React.useMemo(() => {
    switch (AppStore.app.agent) {
      case 'wake':
        return (
          <>
            <Trans
              i18nKey="cashbackPopup.purchase.make.predefinedPromotions.values"
              defaults="Make a purchase over {{purchaseValue, currency}} and get one of the following cashback values"
              values={{
                purchaseValue: AppCashbackStore.highestPromotionAppCashback?.minPurchaseValue,
              }}
            />
            <Stack spacing={0.5} mt={1}>
              {
                AppCashbackStore.appCashbacks?.map((appCashback, index, appCashbacks) => (
                  <Typography sx={{ opacity: 0.5 }} fontSize={14} fontWeight={400} key={appCashback.id}>
                    {index === (appCashbacks.length - 1) ? (
                      <Trans
                        i18nKey="coreloyalty.cashback.predefinedPromotion.widget.promotionDetails.last"
                        defaults="<strong>{{value}} cashback</strong> on orders of <strong>{{rangeStart, currency}} or more</strong>"
                        values={{
                          value: formatCashbackPromotionValue(appCashback),
                          rangeStart: appCashback.minPurchaseValue,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey="coreloyalty.cashback.predefinedPromotion.widget.promotionDetails"
                        defaults="<strong>{{value}} cashback</strong> on orders of <strong>{{rangeStart, currency}} - {{rangeEnd, currency}}</strong>"
                        values={{
                          value: formatCashbackPromotionValue(appCashback),
                          rangeStart: appCashback.minPurchaseValue,
                          rangeEnd: appCashbacks[index + 1].minPurchaseValue,
                        }}
                      />
                    )}
                  </Typography>
                ))
              }
            </Stack>
          </>
        );
      default:
        return (
          <Trans
            i18nKey="cashbackPopup.purchase.make"
            defaults="Make a purchase over {{purchaseValue, currency}} and get {{percentage}}% cashback."
            values={{
              percentage: AppCashbackStore.currentAppCashback?.percentage,
              purchaseValue: AppCashbackStore.currentAppCashback?.minPurchaseValue,
            }}
          />
        );
    }
  }, [
    AppStore.app.agent,
    AppCashbackStore.highestPromotionAppCashback,
    AppCashbackStore.appCashbacks,
  ]);

  return (
    <>
      {title}
      <Typography fontSize={14} fontWeight={600}>
        {description}
      </Typography>
    </>
  );
};

export default observer(CashbackPercentage);
